/**
 * Sidebar Component - Clean Design
 */

import { NavLink, useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores';
import { useEffect } from 'react';

export function Sidebar() {
    const navigate = useNavigate();
    const { currentApp, apps, collections, fetchApps, fetchCollections, setCurrentApp } = useAppStore();

    useEffect(() => {
        fetchApps();
    }, [fetchApps]);

    useEffect(() => {
        if (currentApp?.id) {
            fetchCollections(currentApp.id);
        }
    }, [currentApp?.id, fetchCollections]);

    return (
        <aside
            className="w-64 h-screen flex flex-col z-20 shrink-0"
            style={{ backgroundColor: 'var(--bg-primary)', borderRight: '1px solid var(--border-color)' }}
        >
            {/* Logo Section */}
            <div
                className="h-16 flex items-center px-6 cursor-pointer hover:bg-white/5 transition-colors"
                style={{ borderBottom: '1px solid var(--border-color)' }}
                onClick={() => navigate('/')}
            >
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
                        <span className="text-lg">⚖️</span>
                    </div>
                    <span
                        className="font-bold text-lg tracking-tight"
                        style={{ color: 'var(--text-primary)', fontFamily: 'Outfit, sans-serif' }}
                    >
                        LegalCMS
                    </span>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">

                {/* Project Switcher */}
                <div className="p-4">
                    <div className="px-2 mb-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                        Current Project
                    </div>
                    <div className="relative">
                        <select
                            className="w-full pl-4 pr-10 py-2.5 text-sm font-semibold rounded-xl appearance-none cursor-pointer transition-all"
                            style={{
                                backgroundColor: 'var(--bg-tertiary)',
                                border: '1px solid var(--border-color)',
                                color: 'var(--text-primary)',
                                outline: 'none'
                            }}
                            value={currentApp?.id || ''}
                            onChange={(e) => {
                                const app = apps.find(a => a.id === e.target.value);
                                if (app) {
                                    setCurrentApp(app);
                                    navigate('/');
                                }
                            }}
                        >
                            {apps.map(app => (
                                <option key={app.id} value={app.id}>
                                    {app.icon || '🚀'} &nbsp; {app.name}
                                </option>
                            ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-50 text-xs">
                            ▼
                        </div>
                    </div>
                </div>

                <nav className="flex-1 px-4 space-y-8 py-2">
                    {/* Main Nav */}
                    <div>
                        <div className="px-2 mb-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                            Platform
                        </div>
                        <div className="space-y-1">
                            <NavLink to="/" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
                                <span className="text-lg">📊</span>
                                <span className="font-semibold">Dashboard</span>
                            </NavLink>
                            <NavLink to="/projects" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
                                <span className="text-lg">🚀</span>
                                <span className="font-semibold">All Projects</span>
                            </NavLink>
                        </div>
                    </div>

                    {/* Collection Nav */}
                    {currentApp && (
                        <div>
                            <div className="px-2 mb-2 flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                                    Collections
                                </span>
                                <NavLink
                                    to="/schema"
                                    className="text-[10px] font-bold uppercase hover:text-blue-500 transition-colors"
                                    style={{ color: 'var(--accent)' }}
                                >
                                    + New
                                </NavLink>
                            </div>
                            <div className="space-y-1">
                                {collections.map((col) => (
                                    <NavLink
                                        key={col.id}
                                        to={`/collections/${col.id}`}
                                        className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                                    >
                                        <span className="text-lg opacity-80">{col.icon || '📄'}</span>
                                        <span className="font-medium truncate">{col.name}</span>
                                    </NavLink>
                                ))}
                                {collections.length === 0 && (
                                    <div className="px-4 py-3 rounded-xl border border-dashed text-center" style={{ borderColor: 'var(--border-color)' }}>
                                        <p className="text-[10px] italic" style={{ color: 'var(--text-muted)' }}>
                                            No collections yet
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* System Nav */}
                    <div>
                        <div className="px-2 mb-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                            System
                        </div>
                        <div className="space-y-1">
                            <NavLink to="/media" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
                                <span className="text-lg">🖼️</span>
                                <span className="font-semibold">Media Assets</span>
                            </NavLink>
                            <NavLink to="/settings" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
                                <span className="text-lg">⚙️</span>
                                <span className="font-semibold">Configuration</span>
                            </NavLink>
                        </div>
                    </div>
                </nav>
            </div>

            {/* User Profile Summary */}
            <div className="p-4" style={{ borderTop: '1px solid var(--border-color)' }}>
                <div
                    className="flex items-center gap-3 p-2 rounded-xl"
                    style={{ backgroundColor: 'var(--bg-tertiary)' }}
                >
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-xs font-bold text-blue-500">
                        ⚡
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Storage Bound</div>
                        <div className="text-xs font-bold truncate" style={{ color: 'var(--text-primary)' }}>Cloudflare R2</div>
                    </div>
                </div>
            </div>
        </aside>
    );
}
